// ###### IMPORTS
import {
  apiClient,
  apiClientFileUpload,
  apiClientFileDownload,
} from "connections/apiClient";
import apiRoutes from "connections/apiRoutes";

// ###### API CALLS
// Input Configurations
export async function getInputMetadata(tableId = "") {
  try {
    const response = await apiClient.get(apiRoutes.inputs.metadata, {
      params: tableId ? { table_id: tableId } : undefined,
    });
    console.log("Input Metadata Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch input metadata:", error);
    throw error;
  }
}

export async function getInputColumns(tableId = "") {
  try {
    const response = await apiClient.get(apiRoutes.inputs.columns, {
      params: tableId ? { table_id: tableId } : undefined,
    });

    console.log("Input Columns Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch input columns:", error);
    throw error;
  }
}

export async function getInputFiles(tableId = "") {
  try {
    const response = await apiClient.get(apiRoutes.inputs.files, {
      params: tableId ? { table_id: tableId } : undefined,
    });
    console.log("Input Files Response:", response.data);

    return response.data;
  } catch (error) {
    console.error("Failed to fetch input files:", error);
    throw error;
  }
}

// File Manipulation
export async function sendFilePrepare(file, tableId, headerRow = 1) {
  try {
    if (!file || !tableId) {
      throw new Error("File and tableId are required");
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("table_id", tableId);
    formData.append("header_row", headerRow.toString());

    const response = await apiClientFileUpload.post(
      apiRoutes.inputs.prepare,
      formData,
    );

    console.log("Prepare File Response:", response.data);

    return response.data;
  } catch (error) {
    console.error("Failed to prepare file:", error);
    throw error;
  }
}

export async function sendFileUpload({
  tableId,
  fileName,
  columnMapping,
  variantOperations,
  headerRow = 1,
}) {
  try {
    // Use regular apiClient for JSON data (not file upload client)
    // The file upload client is only for actual file transfers (like file preparation)
    const response = await apiClient.post(apiRoutes.inputs.upload, {
      table_id: tableId,
      file_name: fileName,
      column_mapping: columnMapping,
      variant_operations: variantOperations,
      header_row: headerRow,
    });

    console.log("File Upload Response:", response.data);

    return response.data;
  } catch (error) {
    console.error("Failed to upload file:", error);
    throw error;
  }
}

export async function getPrepareProgress(prepareId) {
  try {
    const response = await apiClient.get(apiRoutes.inputs.uploadProgress, {
      params: { prepare_id: prepareId },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to get prepare progress:", error);
    throw error;
  }
}

export async function getUploadProgress(uploadId) {
  try {
    console.log(`Making API call to get upload progress for ID: ${uploadId}`);
    console.log(`API URL: ${apiRoutes.inputs.uploadProgress}`);

    const response = await apiClient.get(apiRoutes.inputs.uploadProgress, {
      params: { upload_id: uploadId },
    });

    console.log("Upload Progress Response:", response.data);

    return response.data;
  } catch (error) {
    console.error("Failed to get upload progress:", error);
    console.error("Error details:", {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      headers: error.config?.headers,
    });
    throw error;
  }
}
// Data from BigQuery
export async function getTableData(tableId, variantId, limit = 1000) {
  try {
    const response = await apiClient.get(apiRoutes.inputs.tableData, {
      params: {
        table_id: tableId,
        variant_id: variantId,
        limit: limit,
      },
    });

    console.log("Table Data Response:", response.data);

    return response.data;
  } catch (error) {
    console.error("Failed to fetch table data:", error);
    throw error;
  }
}

export async function getIngestionColumns(tableId = "") {
  try {
    const response = await apiClient.get(apiRoutes.inputs.ingestionColumns, {
      params: tableId ? { table_id: tableId } : undefined,
    });
    console.log("Ingestion Columns Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch ingestion columns:", error);
    throw error;
  }
}

export async function getVariants() {
  try {
    const response = await apiClient.get(apiRoutes.inputs.variants);
    console.log("Variants Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch variants:", error);
    throw error;
  }
}

export async function downloadHistoricalFile(tableId, fileName) {
  try {
    const response = await apiClientFileDownload.get(
      apiRoutes.inputs.downloadHistorical,
      {
        params: { table_id: tableId, file_name: fileName },
        responseType: "blob",
      },
    );

    // Create blob link and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return response.data;
  } catch (error) {
    console.error("Failed to download historical file:", error);
    throw error;
  }
}

export async function createVariant(tableId, variantName) {
  try {
    const response = await apiClient.post(apiRoutes.inputs.variants, {
      table_id: tableId,
      variant_name: variantName,
    });
    console.log("Create Variant Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to create variant:", error);
    throw error;
  }
}

export async function updateVariantTags(tableId, variantId, tags) {
  try {
    const response = await apiClient.patch(apiRoutes.inputs.variantTags, {
      table_id: tableId,
      variant_id: variantId,
      tags: tags,
    });
    console.log("Update Variant Tags Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to update variant tags:", error);
    throw error;
  }
}

function getFilenameFromDisposition(contentDisposition, fallback) {
  const match = contentDisposition?.match(/filename="?([^"]+)"?/);
  return match?.[1] || fallback;
}

export async function downloadVariantSourceFile(tableId, variantId) {
  try {
    const response = await apiClientFileDownload.get(
      `/api/inputs/${tableId}/variants/${variantId}/download/`,
      { responseType: "blob" },
    );

    const fileName = getFilenameFromDisposition(
      response.headers?.["content-disposition"],
      `${tableId}_variant_${variantId}.xlsx`,
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return response.data;
  } catch (error) {
    console.error("Failed to download variant source file:", error);
    throw error;
  }
}

export async function renameVariant(tableId, variantId, variantName) {
  try {
    const response = await apiClient.patch(
      `/api/inputs/${tableId}/variants/${variantId}/rename/`,
      { variant_name: variantName },
    );
    console.log("Rename Variant Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to rename variant:", error);
    throw error;
  }
}

export async function deleteVariant(tableId, variantId) {
  try {
    const response = await apiClient.delete(
      `/api/inputs/${tableId}/variants/${variantId}/`,
    );
    console.log("Delete Variant Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to delete variant:", error);
    throw error;
  }
}

export async function getInputSanitizationRules(tableId) {
  try {
    const response = await apiClient.get(apiRoutes.inputs.sanitizationRules, {
      params: tableId ? { table_id: tableId } : undefined,
    });
    console.log("Input Sanitization Rules Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch input sanitization rules:", error);
    throw error;
  }
}

export async function createInputSanitizationRule(payload) {
  try {
    const response = await apiClient.post(
      apiRoutes.inputs.sanitizationRules,
      payload,
    );
    console.log("Create Input Sanitization Rule Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to create input sanitization rule:", error);
    throw error;
  }
}

export async function updateInputSanitizationRule(payload) {
  try {
    const response = await apiClient.patch(
      apiRoutes.inputs.sanitizationRules,
      payload,
    );
    console.log("Update Input Sanitization Rule Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to update input sanitization rule:", error);
    throw error;
  }
}

export async function disableInputSanitizationRule(ruleId) {
  try {
    const response = await apiClient.delete(
      apiRoutes.inputs.sanitizationRules,
      {
        data: { rule_id: ruleId },
      },
    );
    console.log("Disable Input Sanitization Rule Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to disable input sanitization rule:", error);
    throw error;
  }
}

export async function deleteInputSanitizationRule(ruleId) {
  try {
    const response = await apiClient.delete(
      apiRoutes.inputs.sanitizationRules,
      {
        data: { rule_id: ruleId, hard_delete: true },
      },
    );
    console.log("Delete Input Sanitization Rule Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to delete input sanitization rule:", error);
    throw error;
  }
}

export async function getModelDimensions(tableId = "model01") {
  try {
    const response = await apiClient.get(apiRoutes.inputs.modelDimensions, {
      params: { table_id: tableId },
    });
    console.log("Model Dimensions Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch model dimensions:", error);
    throw error;
  }
}

export async function updateInputDescription(tableId, descriptionFull) {
  try {
    const response = await apiClient.patch(apiRoutes.inputs.description, {
      table_id: tableId,
      table_description_full: descriptionFull,
    });
    console.log("Update Input Description Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to update input description:", error);
    throw error;
  }
}

export async function getAvailableInputsForModel01() {
  try {
    const response = await apiClient.get(apiRoutes.inputs.model01Available);
    console.log("Available Inputs for Model01 Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch available inputs for model01:", error);
    throw error;
  }
}

export async function getDimensionUniqueValues(dimension, variantId) {
  try {
    const response = await apiClient.get(
      apiRoutes.inputs.model01DimensionValues,
      {
        params: { dimension, variant_id: variantId },
      },
    );
    console.log(`Dimension ${dimension} unique values:`, response.data);
    return response.data;
  } catch (error) {
    console.error(
      `Failed to fetch dimension ${dimension} unique values:`,
      error,
    );
    throw error;
  }
}

export async function generateModel01(payload) {
  try {
    const response = await apiClient.post(
      apiRoutes.inputs.model01Generate,
      payload,
      {
        responseType: "blob",
      },
    );
    console.log("Model01 Generation Response received");
    return response.data;
  } catch (error) {
    console.error("Failed to generate model01:", error);

    // When responseType is 'blob', error messages come as blob too
    // We need to convert blob to text to get the actual error message
    if (error.response && error.response.data instanceof Blob) {
      let errorData;
      try {
        const text = await error.response.data.text();
        errorData = JSON.parse(text);
      } catch (parseError) {
        // If parsing fails, throw original error
        console.error("Failed to parse error blob:", parseError);
        throw error;
      }

      // Create a new error with the backend message
      const backendError = new Error(errorData.error || error.message);
      backendError.response = {
        ...error.response,
        data: errorData,
      };
      throw backendError;
    }

    throw error;
  }
}

// sendFileCleanup function removed - temp blob cleanup is now handled by natural overwrite behavior
// This simplifies the system and relies on the robust temp storage design where each table has max 1 temp file
